import type { CityConfig } from "../city-template/types";

export const fairmontConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Fairmont",
  state: "West Virginia",
  stateAbbr: "WV",
  slug: "fairmont",
  route: "/cities/fairmont",
  title: "Emergency Passport Fairmont, WV — Expedited Same-Day Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Fairmont, WV — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Marion County, Fairmont State University, and I-79 corridor travelers via Pittsburgh and Morgantown airports.",
  h1: "Emergency & Expedited Passport Services in Fairmont, WV",
  heroSubtitle: "Pittsburgh flight to Rome in 10 days? Fairmont State study abroad to London next month? NASA IV&V tech conference in Tokyo? We help Fairmont residents — from downtown professionals to Fairmont State students to Pleasant Valley families — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Fairmont%20West%20Virginia%20historic%20downtown%20district%20along%20the%20Monongahela%20River%2C%20historic%20brick%20buildings%20and%20the%20Marion%20County%20Courthouse%20with%20its%20iconic%20clock%20tower%2C%20tree-lined%20streets%20with%20Appalachian%20hills%20in%20the%20background%2C%20warm%20afternoon%20sunlight%2C%20small%20city%20nestled%20in%20the%20river%20valley%20with%20bridges%20and%20green%20hillsides&width=1800&height=700&seq=fairmont-hero-2026&orientation=landscape",
  heroImageAlt: "Fairmont West Virginia historic downtown along Monongahela River with Marion County Courthouse",
  geoPosition: "39.4851;-80.1426",
  geoPlacename: "Fairmont, West Virginia",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Detroit Passport Agency",
    address: "211 West Fort Street, Detroit, MI 48226",
    distance: "Approximately 330 miles from Fairmont — appointments required, typically booked 2+ weeks out. Pittsburgh is the more practical airport but has limited agency hours.",
  },
  nearbyCitiesZips: [
    { name: "Fairmont", zips: "26554, 26555" },
    { name: "Pleasant Valley", zips: "26554" },
    { name: "White Hall", zips: "26554" },
    { name: "Mannington", zips: "26582" },
    { name: "Monongah", zips: "26554" },
    { name: "Barrackville", zips: "26559" },
    { name: "Farmington", zips: "26571" },
    { name: "Rivesville", zips: "26588" },
    { name: "Bridgeport", zips: "26330" },
    { name: "Clarksburg", zips: "26301, 26302" },
  ],
  benefits: [
    { icon: "ri-flight-takeoff-line", title: "I-79 Corridor — Pittsburgh Airport Access", desc: "Fairmont sits directly on I-79, putting Pittsburgh International Airport (PIT) just 85 miles north. Direct international flights to Cancun, Punta Cana, London, and Frankfurt. We build PIT timelines into every passport plan for Marion County travelers." },
    { icon: "ri-graduation-cap-line", title: "Fairmont State University Student Hub", desc: "Fairmont State University and Pierpont Community & Technical College generate steady demand for first-time passports from students studying abroad, international mission trips, and spring break international travel." },
    { icon: "ri-rocket-line", title: "NASA IV&V & Tech Sector Travel", desc: "Fairmont is home to NASA's Katherine Johnson Independent Verification and Validation Facility — a major federal tech employer generating international travel for conferences, contractor meetings, and collaborative projects." },
    { icon: "ri-train-line", title: "Morgantown Proximity Advantage", desc: "Just 18 miles south of Morgantown and WVU, Fairmont residents can also use Morgantown acceptance facilities and the Morgantown Municipal Airport (MGW) for regional connections when PIT isn't convenient." },
  ],
  testimonials: [
    {
      name: "Jennifer H.",
      location: "Downtown Fairmont",
      rating: 5,
      text: "NASA IV&V sent me to a software assurance conference in Tokyo with 14 days notice. My passport was expired and I commute between Fairmont and the facility daily. They handled everything remotely and my passport arrived in 8 days.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Tyler W.",
      location: "Fairmont State University",
      rating: 5,
      text: "FSU study abroad to London — first passport ever. The student checklist was super helpful, the Fairmont post office visit was quick, and my passport arrived well before the program orientation. Made the whole process stress-free.",
      service: "New Passport — First Time",
    },
    {
      name: "The Patel Family",
      location: "Pleasant Valley",
      rating: 5,
      text: "Trip to India for a family wedding from Pittsburgh — realized two of us needed passport renewals and one needed a new child passport. They handled all three applications and we had everything in 12 days. Saved the wedding trip.",
      service: "Multiple Passport Services",
    },
    {
      name: "Robert C.",
      location: "White Hall",
      rating: 5,
      text: "Business trip to Frankfurt for a tech sector supplier meeting with 8 days notice. Needed a second passport since my primary was with the German consulate for visa processing. Expedited second passport arrived in time for the flight.",
      service: "Second Passport — Expedited",
    },
  ],
  facilities: [
    {
      id: "fairmont-1",
      name: "Fairmont Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "331 Cleveland Ave",
      city: "Fairmont",
      state: "WV",
      zip: "26554",
      phone: "(304) 363-1756",
      hours: "Mon–Fri 9:00 AM – 3:30 PM (appointment required)",
      distance: "Fairmont — Cleveland Avenue",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Primary acceptance facility for Fairmont and Marion County. Passport photos available on-site. Appointment required — call ahead during summer travel season as hours are limited.",
      icon: "ri-mail-line",
    },
    {
      id: "fairmont-2",
      name: "Marion County Circuit Clerk",
      type: "County Clerk Passport Acceptance",
      address: "219 Adams St, Room 211",
      city: "Fairmont",
      state: "WV",
      zip: "26554",
      phone: "(304) 367-5360",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      distance: "Downtown Fairmont — Marion County Courthouse",
      website: "https://www.marioncountywv.com/circuitclerk",
      websiteLabel: "Marion County Clerk",
      notes: "County clerk passport acceptance counter in the Marion County Courthouse. Often less crowded than the post office. No photo services on-site — obtain passport photos before visiting.",
      icon: "ri-government-line",
    },
    {
      id: "fairmont-3",
      name: "Morgantown Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "215 High St",
      city: "Morgantown",
      state: "WV",
      zip: "26505",
      phone: "(304) 296-3670",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Morgantown — 18 miles north, Monongalia County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Alternative facility in Morgantown with more appointment availability. Convenient for Fairmont residents who work or attend classes in Morgantown. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "fairmont-4",
      name: "Bridgeport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 W Main St",
      city: "Bridgeport",
      state: "WV",
      zip: "26330",
      phone: "(304) 842-6061",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Bridgeport — 15 miles south, Harrison County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Serving Harrison County and southern Marion County residents. Larger facility with more appointment slots. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "fairmont-5",
      name: "Fairmont State University — Study Abroad Office",
      type: "University International Programs",
      address: "1201 Locust Ave, Hardway Hall",
      city: "Fairmont",
      state: "WV",
      zip: "26554",
      phone: "(304) 367-4892",
      hours: "Mon–Fri 8:00 AM – 4:00 PM (academic year)",
      distance: "Fairmont State University campus",
      website: "https://www.fairmontstate.edu/academics/studyabroad",
      websiteLabel: "FSU Study Abroad",
      notes: "Not a passport acceptance facility but provides study abroad guidance and can direct students to the correct passport resources. Coordinate with the study abroad office early — passport processing should start when you receive your program acceptance.",
      icon: "ri-graduation-cap-line",
    },
  ],
  faqs: [
    { q: "How fast can I get a passport in Fairmont?", a: "We offer Fairmont-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The nearest passport agency is in Detroit (330 miles) — making courier service the faster and far more practical option for Marion County residents." },
    { q: "Do I need to visit an office in Fairmont?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (Fairmont Main Post Office or Marion County Clerk), then ship documents overnight. We handle the State Department submission and return your passport to your Fairmont address." },
    { q: "Which airport should Fairmont travelers use?", a: "Pittsburgh International Airport (PIT, ~85 miles north via I-79) is the primary choice for international travel from Fairmont. Direct flights to Cancun, Punta Cana, London, and Frankfurt. Morgantown Municipal Airport (MGW, 18 miles) offers regional connections to Pittsburgh and DC for domestic connections to international gateways." },
    { q: "Does Fairmont State University offer passport services?", a: "Fairmont State does not have a dedicated passport office, but the university's Study Abroad office in Hardway Hall can direct students to passport resources. The Fairmont Main Post Office and the Marion County Clerk both serve as acceptance facilities. We provide student-specific guidance for study abroad and mission trips." },
    { q: "What is the best price for expedited passport service in Fairmont?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. Best price guarantee included." },
    { q: "Where can I get passport photos in Fairmont?", a: "Walmart Supercenter on Fairmont Avenue, CVS Pharmacy on Fairmont Avenue, and the Fairmont Main Post Office all offer passport photos. Walgreens in Morgantown (18 miles north) and the UPS Store in Bridgeport (15 miles south) also provide photo services." },
    { q: "Can I use Morgantown acceptance facilities?", a: "Yes — many Fairmont residents use Morgantown post office and acceptance facilities, especially if they work or attend classes in Morgantown (just 18 miles away). These are fully valid options and often have more appointment availability than Fairmont facilities." },
    { q: "What about NASA IV&V employee passport needs?", a: "The NASA Katherine Johnson IV&V Facility in Fairmont employs software engineers and contractors who travel internationally for conferences, collaborative projects, and interagency meetings. Federal travel authorizations often come with short notice. We understand government travel timelines and handle NASA contractor passports with the care required." },
    { q: "How does Fairmont State study abroad affect passport timing?", a: "FSU study abroad acceptances typically arrive 6–8 weeks before departure. For students needing a first passport, this is enough time for standard expedited processing — but start immediately upon acceptance. For renewal or emergency situations, we can process faster. Coordinate with the FSU Study Abroad office for program-specific requirements." },
    { q: "How do I check my passport status from Fairmont?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online status system. Processing from submission to return typically matches your chosen speed tier." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Fairmont Ave", address: "2701 Fairmont Ave, Fairmont, WV 26554", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "CVS Pharmacy — Fairmont Ave", address: "201 Fairmont Ave, Fairmont, WV 26554", hours: "Daily 8 AM – 9 PM", website: "https://www.cvs.com/" },
    { name: "Fairmont Main Post Office", address: "331 Cleveland Ave, Fairmont, WV 26554", hours: "Mon–Fri 9 AM – 3:30 PM", website: "https://tools.usps.com/find-location.htm" },
    { name: "Walgreens — Morgantown", address: "350 Patteson Dr, Morgantown, WV 26505", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "The UPS Store — Bridgeport", address: "1240 W Main St, Bridgeport, WV 26330", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 9 AM – 1 PM", website: "https://www.theupsstore.com/" },
  ],
  dmvLocations: [
    {
      name: "WV DMV — Fairmont Regional Office",
      address: "45 Tygart Mall Loop, White Hall, WV 26554",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, state ID cards. Located in White Hall near I-79. Appointment recommended.",
      featured: true,
    },
  ],
  dmvWebsite: "https://transportation.wv.gov/dmv/",
  dmvLabel: "West Virginia DMV — Official Website",
  airports: [
    {
      code: "PIT",
      name: "Pittsburgh International Airport",
      distance: "About 85 miles north of Fairmont via I-79",
      address: "1000 Airport Blvd, Pittsburgh, PA 15231",
      note: "Primary international gateway for Fairmont and Marion County travelers. Direct flights to Cancun, Punta Cana, London, Frankfurt, and Caribbean destinations. About 1 hour 15 minutes via I-79.",
      flightUrl: "https://flypittsburgh.com/",
      flightLabel: "Pittsburgh Airport Website",
      featured: true,
    },
    {
      code: "MGW",
      name: "Morgantown Municipal Airport",
      distance: "About 18 miles north of Fairmont",
      address: "100 Hart Field Rd, Morgantown, WV 26505",
      note: "Regional airport with connections to Pittsburgh and Washington Dulles for international gateway access. About 25 minutes via I-79. Convenient for domestic connections to international hubs.",
      flightUrl: "https://www.morgantownwv.gov/261/Airport",
      flightLabel: "Morgantown Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate — WV DHHR",
      desc: "Request certified birth certificates from the West Virginia DHHR Vital Registration Office. Online, mail, or in-person options.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate — Marion County",
      desc: "Request marriage license records from the Marion County Clerk. Certified copies for name changes and passport applications.",
      url: "https://www.marioncountywv.com/countyclerk",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree — Marion County",
      desc: "Obtain certified divorce records through the Marion County Circuit Clerk for name change passport corrections.",
      url: "https://www.marioncountywv.com/circuitclerk",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate — WV DHHR",
      desc: "Certified death certificates through WV DHHR for estate and legal matters.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Pittsburgh flight to Rome in two days? NASA conference in Tokyo with short notice? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Fairmont residents renew fast — no agency visit required. Perfect for FSU faculty, NASA contractors, and Marion County families.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport — First Time",
      description: "First-time applicants require an in-person acceptance visit. We guide Fairmont residents through every step with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require DS-64 and additional documentation. We guide you through everything to avoid costly mistakes — so your trip or study abroad stays on track.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear checklists so your family vacation stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or legal name change — we assist with corrected passports so your travel documents match IDs and reservations.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for NASA contractors and tech professionals traveling while primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Marion County travelers through the process.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "Wallet-size travel document for land and sea crossings to Canada, Mexico, Caribbean, and Bermuda — expedited with the same courier service.",
      bullets: ["Wallet-size convenience", "Land and sea travel", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Fairmont WV", description: "Urgent passport processing for Fairmont West Virginia travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Fairmont WV", description: "Fast passport renewal for Fairmont West Virginia residents." },
    { name: "First-Time Passport Fairmont WV", description: "New passport application services for Fairmont West Virginia residents." },
    { name: "Child Passport Fairmont WV", description: "Minor passport services for Fairmont West Virginia families." },
    { name: "Name Change Passport Fairmont WV", description: "Passport name correction for Fairmont residents." },
    { name: "Second Passport Fairmont WV", description: "Second passport services for Fairmont tech and business travelers." },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Waiting Until After Study Abroad Acceptance",
      description: "Fairmont State students often wait until they receive their study abroad acceptance before starting the passport process. By the time the program orientation arrives, there's only 4–6 weeks left — a tight window for first-time passports. Start the passport process as soon as you apply to study abroad, not after acceptance.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Assuming Pittsburgh is Too Far for a Quick Trip",
      description: "At 85 miles, PIT is closer than many Fairmont residents realize — about 1 hour 15 minutes via I-79. But don't count on driving to Pittsburgh for a last-minute passport agency appointment. The nearest agency is in Detroit (330 miles). Our courier service is faster and eliminates the drive entirely.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Mixing Up Fairmont State and WVU Passport Resources",
      description: "Fairmont State students sometimes try to use WVU's passport services in Morgantown, but WVU resources are for WVU students only. FSU students should use Fairmont acceptance facilities and our remote courier service for the actual processing.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Photos from local pharmacies and big-box stores frequently fail State Department standards. We review all photos before submission to prevent rejection and delay.",
    },
    {
      icon: "ri-error-warning-line",
      title: "NASA Contractor Clearance vs. Passport Confusion",
      description: "NASA IV&V contractors sometimes confuse their facility security clearance with passport requirements. A government clearance does not replace or accelerate passport processing. Travel-ready passport and clearance are separate — you need both independently.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order — just a few minutes from home, office, or campus." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist tailored to your departure airport and specific situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using overnight shipping. Most Fairmont clients use the UPS Store in Bridgeport or ship from the Fairmont post office." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency channel — faster than a Detroit agency appointment, no 330-mile drive required." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport arrives at your Fairmont address within your selected timeframe. No long drive, no appointment scramble." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — nearest is Detroit (330 miles)", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review", "No dedicated agent", "Updates only by mail", "Can't upgrade speed after submission"],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — Detroit agency is 330 miles away. Booked 2+ weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 330-mile drive to Detroit", fpc: "Most Fairmont travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission" },
    { feature: "Local Guidance", agency: "Generic — no Marion County, FSU, or NASA IV&V expertise", fpc: "Full Fairmont/I-79/PIT knowledge" },
    { feature: "Cost vs Resellers", agency: "Government fees only — you handle all logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Fast passport services in Charleston, WV", route: "/cities/charleston" },
    { name: "Expedited passport in Morgantown, WV", route: "/cities/morgantown" },
    { name: "Quick passport services in Clarksburg, WV", route: "/cities/clarksburg" },
    { name: "Urgent passport services in Pittsburgh, PA", route: "/cities/pittsburgh" },
    { name: "West Virginia passport services overview", route: "/states/west-virginia" },
  ],
  fpcOfficeAddress: "1629 K Street, Suite 300, Washington, DC 20006",
  fpcOfficeNote: "Fast Passport Center serves Fairmont via secure registered courier with direct access to regional passport agencies. Our registered couriers hand-deliver your documents — meaning you get faster results without the 330-mile Detroit drive. Fairmont residents can ship from any UPS Store, FedEx Office, or post office in Marion County.",
  fpcOfficeDistance: "Serving Fairmont, Pleasant Valley, White Hall, Mannington, Monongah, Barrackville, and all Marion County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Fairmont and Marion County. From downtown Fairmont to Pleasant Valley, from Fairmont State University to White Hall — we serve all Marion County communities. Our courier service provides faster processing than a Detroit agency appointment, without the 330-mile drive.",
  nearbyCitiesIntro: "We serve Fairmont and all Marion County communities including Pleasant Valley, White Hall, Mannington, Monongah, Barrackville, Farmington, and Rivesville. We also serve nearby communities in Harrison County (Bridgeport, Clarksburg) and Monongalia County (Morgantown). Travelers depart from Pittsburgh International Airport.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Fairmont as simple and predictable as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you. Most Fairmont travelers start online and ship documents overnight — perfect for students, NASA professionals, and Marion County families.",
  mistakesIntro: "Fairmont travelers encounter unique passport issues — from study abroad timing to NASA contractor clearance confusion to I-79 travel logistics. Here are the most common mistakes we see.",
  testimonialsIntro: "Fairmont travelers — from Fairmont State University students and NASA IV&V professionals to Marion County families — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Fairmont, WV? Here are the most common ones from Marion County travelers.",
  statePageRoute: "/states/west-virginia",
  statePageName: "West Virginia",
  servicesIntro: "Whether you need a renewal before a Pittsburgh flight to Europe, an emergency passport for a NASA conference, or a first-time passport for Fairmont State study abroad — Fast Passport Center handles it with reserved courier submission. As a registered State Department courier, we guarantee the best price.",
  travelTiming: {
    title: "Travel & Passport Timing for Fairmont Residents",
    intro: "Fairmont's location on the I-79 corridor with dual airport access and a major university creates distinct travel patterns. Here's what Fairmont travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PIT — Pittsburgh International Airport",
        subtitle: "85 miles north via I-79, primary international gateway",
        description: "Pittsburgh International is the go-to for Fairmont international travel with direct flights to Cancun, Punta Cana, London (seasonal), and Frankfurt. The I-79 drive takes about 1 hour 15 minutes. For international check-in, plan for 3.5 hours door to gate from Fairmont — 90 minutes drive, 2 hours for check-in and security.",
        tip: "I-79 is generally a reliable highway but can get foggy in the Marion County valleys during fall and winter mornings. For early international flights, consider staying near the airport the night before during November–February.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "Fairmont State University Travel Calendar",
        subtitle: "Study abroad, spring break, and mission trips",
        description: "FSU's academic calendar drives a predictable passport rush. Fall study abroad applications close in March–April for August departure. Spring programs close in October for January departure. Spring break international trips surge in February–March. Summer mission trips peak in April–May. Student passport demand follows these cycles — plan 8+ weeks ahead during these windows.",
        tip: "Apply for your passport as soon as you submit your study abroad application — don't wait for acceptance. If your program is in Europe, you'll also need to budget time for any required student visas after receiving your passport.",
      },
      {
        icon: "ri-rocket-line",
        title: "NASA IV&V & Federal Contractor Travel",
        subtitle: "Short-notice government and tech sector trips",
        description: "Fairmont's NASA Katherine Johnson IV&V Facility employs hundreds of software engineers and federal contractors who travel internationally for verification conferences, collaborative projects, and interagency meetings. These trips often come with 7–14 days notice — the standard for federal travel authorizations.",
        tip: "Federal travel orders are independent of passport readiness. Maintain a current passport with at least 6 months validity — your agency's travel system won't flag an upcoming expiration. Government clearances and passports are managed separately.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "I-79 Corridor = Pittsburgh Access, Detroit Distance",
      description: "Fairmont's I-79 location means easy access to Pittsburgh International Airport (85 miles) but significant distance to the nearest passport agency (Detroit, 330 miles). Our courier service bridges this gap — giving you faster processing than any agency appointment without the drive.",
    },
  },
  destinations: {
    title: "Where Fairmont Travelers Go",
    intro: "Fairmont's university community, NASA workforce, and I-79 corridor location create distinct international travel patterns.",
    stats: [
      { stat: "35%", label: "of Fairmont expedited requests are student or university-related" },
      { stat: "1–2 Weeks", label: "average lead time for NASA and federal contractor travel bookings" },
      { stat: "PIT", label: "dominant airport for 90%+ of Fairmont international departures" },
      { stat: "6 Days", label: "shortest turnaround for an FSU student accepted to London study abroad" },
    ],
    destinations: [
      { name: "United Kingdom", context: "FSU study abroad to London, PIT-London seasonal direct, NASA collaborative projects", urgency: "Study abroad acceptances 6–8 weeks before departure", icon: "ri-map-pin-2-line" },
      { name: "Germany", context: "PIT-Frankfurt direct, tech sector supplier visits, engineering conferences", urgency: "Conference and business travel typically 2–4 weeks notice", icon: "ri-map-pin-2-line" },
      { name: "Japan & South Korea", context: "NASA IV&V software assurance conferences, tech sector collaborations", urgency: "Federal travel authorizations 7–14 days before departure", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Caribbean", context: "PIT direct flights, spring break trips, family resort vacations", urgency: "Spring break bookings peak January–February", icon: "ri-map-pin-2-line" },
      { name: "Italy & Spain", context: "FSU summer programs, European leisure travel, academic conferences", urgency: "Summer study abroad applications close in March–April", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "PIT-Toronto direct, academic conferences, cross-border professional meetings", urgency: "Conference dates fixed, typically 4–8 weeks notice", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other West Virginia Service Areas",
    crossLinksDesc: "We serve travelers across the Mountain State.",
    crossLinks: [
      { name: "Charleston", route: "/cities/charleston" },
      { name: "Huntington", route: "/cities/huntington" },
    ],
  },
};